import { c as _c } from "react/compiler-runtime";
import { NATIVE_CURRENCY_ADDRESS } from "@opensea/app-kit";
import { graphql, useQuery } from "@opensea/graphql";
import { BottomSheetBody, BottomSheetHeader, BottomSheetHeaderTitle, Flex, Separator, TextBody, useToasts } from "@opensea/ui-kit";
import { useCallback, useMemo, useState } from "react";
import { useTranslations } from "use-intl";
import { useSnapshot } from "valtio";
import { MAX_PRICE_CHANGE_PERCENTAGE, PriceChangeError, usePriceValidation } from "../../../components/PriceChange";
import { buildPricingLookup } from "../../../components/QuoteReceipt";
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants";
import { useAddress } from "../../../polychain/hooks/useAddress";
import { FlowActions, FlowState } from "../../../stores/flowStore";
import { useAllActionsCompleted } from "../../hooks/useAllActionsCompleted";
import { useInsufficientFundsActionError } from "../../hooks/useErrors";
import { useSetItemImageMap, useSetItemImageMapFragment } from "../../hooks/useSetItemImageMap";
import { useSetQuoteItems, useSetQuoteItemsFragment } from "../../hooks/useSetQuoteItems";
import { useSetTotalUsdPrice, useSetTotalUsdPriceFragment } from "../../hooks/useSetTotalUsdPrice";
import { CheckoutPaymentSection } from "../../PaymentSection/CheckoutPaymentSection";
import { CheckoutToast } from "../../ToastHandler/CheckoutToast/CheckoutToast";
import { CheckoutFlowSkeleton } from "./CheckoutFlowSkeleton";
import { CheckoutReceipt } from "./components/CheckoutReceipt";
import { CheckoutReceiptFragment } from "./components/CheckoutReceipt/CheckoutReceipt.fragment";
import { CheckoutFlowStatus } from "./components/CheckoutStatus/CheckoutStatus";
import { CheckoutTimeline } from "./components/CheckoutTimeline";
import { CheckoutTimeline_quoteFragment } from "./components/CheckoutTimeline/CheckoutTimeline.fragment";
import { CurrencySelector } from "./components/CheckoutTimeline/CurrencySelector";
import { CurrencySelector_relayerFulfillmentFragment } from "./components/CheckoutTimeline/CurrencySelector.fragment";
const CheckoutFlowQuery = graphql(`
    query CheckoutFlowQuery(
      $itemQuantities: [ItemQuantityInput!]!
      $address: Address
      $substituteItems: Boolean
    ) {
      buyQuote(
        itemQuantities: $itemQuantities
        address: $address
        substituteItems: $substituteItems
      ) {
        supportsFiatCheckout
        supportsRelayerFulfillment {
          ...CurrencySelector_relayerFulfillment
        }
        itemQuotes {
          ... on ItemQuote {
            pricePerItem {
              token {
                contractAddress
                chain {
                  identifier
                }
              }
            }
          }
          ...useSetQuoteItems
          ...useSetItemImageMap
          ...useSetTotalUsdPrice
        }
        ...CheckoutReceipt
        ...CheckoutTimeline_quote
      }
    }
  `, [CheckoutReceiptFragment, CheckoutTimeline_quoteFragment, useSetQuoteItemsFragment, useSetItemImageMapFragment, useSetTotalUsdPriceFragment, CurrencySelector_relayerFulfillmentFragment]);
export function CheckoutFlow(t0) {
  const $ = _c(38);
  const {
    items: itemsWithPricing,
    slippage
  } = t0;
  const t = useTranslations("wallet.CheckoutFlow");
  const address = useAddress();
  const {
    addCustomToast
  } = useToasts();
  let t1;
  let t2;
  if ($[0] !== itemsWithPricing) {
    t2 = buildPricingLookup(itemsWithPricing);
    $[0] = itemsWithPricing;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  t1 = t2;
  const initialPrices = t1;
  const {
    flow,
    itemQuotes
  } = useSnapshot(FlowState);
  const mode = flow?.mode;
  const isClaim = flow?.isClaim ?? false;
  const crossChain = flow?.crossChain ?? false;
  const [selectedCurrency, setSelectedCurrency] = useState(undefined);
  const [balancesReady, setBalancesReady] = useState(false);
  const [walletDestination, setWalletDestination] = useState(undefined);
  useInsufficientFundsActionError();
  let t3;
  if ($[2] !== itemsWithPricing) {
    t3 = itemsWithPricing.map(_temp);
    $[2] = itemsWithPricing;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = Boolean(slippage);
  let t5;
  if ($[4] !== address || $[5] !== t3 || $[6] !== t4) {
    t5 = {
      query: CheckoutFlowQuery,
      variables: {
        itemQuantities: t3,
        address,
        substituteItems: t4
      },
      context: NETWORK_ONLY_OPERATION_CONTEXT
    };
    $[4] = address;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const [t6] = useQuery(t5);
  const {
    data,
    fetching,
    error
  } = t6;
  useSetQuoteItems(data?.buyQuote.itemQuotes);
  useSetItemImageMap(data?.buyQuote.itemQuotes);
  const totalUsdPrice = useSetTotalUsdPrice(data?.buyQuote.itemQuotes);
  let t7;
  if ($[8] !== itemQuotes || $[9] !== itemsWithPricing) {
    t7 = {
      itemsWithPricing,
      flow: "checkout",
      itemQuotes
    };
    $[8] = itemQuotes;
    $[9] = itemsWithPricing;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const priceValidation = usePriceValidation(t7);
  const firstItemQuote = data?.buyQuote.itemQuotes[0];
  let t8;
  if ($[11] !== firstItemQuote || $[12] !== itemsWithPricing[0]) {
    t8 = firstItemQuote?.__typename === "ItemQuote" ? {
      contractAddress: firstItemQuote.pricePerItem.token.contractAddress,
      chainIdentifier: firstItemQuote.pricePerItem.token.chain.identifier
    } : {
      contractAddress: NATIVE_CURRENCY_ADDRESS,
      chainIdentifier: itemsWithPricing[0].chain.identifier
    };
    $[11] = firstItemQuote;
    $[12] = itemsWithPricing[0];
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const defaultCurrency = t8;
  const [allActionsCompleted] = useAllActionsCompleted();
  let t9;
  if ($[14] !== addCustomToast || $[15] !== crossChain || $[16] !== isClaim || $[17] !== mode) {
    t9 = (_response, receiptTransactions) => {
      if (mode === "minimized" && receiptTransactions.length > 0) {
        const {
          itemQuotes: itemQuotes_0,
          itemImageMap,
          flow: flow_0
        } = FlowState;
        addCustomToast(() => <CheckoutToast crossChain={crossChain} flow={flow_0} isClaim={isClaim} itemImageMap={itemImageMap} itemQuotes={itemQuotes_0} transactionIdentifiers={receiptTransactions} />, {
          id: "checkout-toast",
          duration: Number.POSITIVE_INFINITY
        });
        FlowActions.endFlow();
      }
    };
    $[14] = addCustomToast;
    $[15] = crossChain;
    $[16] = isClaim;
    $[17] = mode;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleAllActionsCompleted = t9;
  if (fetching) {
    let t10;
    if ($[19] !== isClaim || $[20] !== itemsWithPricing.length) {
      t10 = <CheckoutFlowSkeleton count={itemsWithPricing.length} isClaim={isClaim} />;
      $[19] = isClaim;
      $[20] = itemsWithPricing.length;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    return t10;
  }
  if (!data?.buyQuote || error) {
    throw new Error("There was a problem with checkout.");
  }
  if (priceValidation.changedItems.some(_temp2)) {
    let t10;
    if ($[22] !== priceValidation) {
      t10 = <PriceChangeError priceValidation={priceValidation} />;
      $[22] = priceValidation;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    return t10;
  }
  let t10;
  if ($[24] !== allActionsCompleted || $[25] !== balancesReady || $[26] !== data || $[27] !== defaultCurrency || $[28] !== handleAllActionsCompleted || $[29] !== initialPrices || $[30] !== isClaim || $[31] !== itemsWithPricing[0] || $[32] !== priceValidation || $[33] !== selectedCurrency || $[34] !== t || $[35] !== totalUsdPrice || $[36] !== walletDestination) {
    t10 = <>{allActionsCompleted ? <BottomSheetBody><CheckoutFlowStatus walletDestination={walletDestination} /></BottomSheetBody> : <><BottomSheetHeader><Flex className="flex-col"><BottomSheetHeaderTitle>{isClaim ? t("claimTitle") : t("title")}</BottomSheetHeaderTitle>{isClaim && <TextBody className="mt-1 text-text-secondary" size="sm">{t("claimSubtext")}</TextBody>}</Flex></BottomSheetHeader><BottomSheetBody><Flex className="size-full flex-col gap-6 md:flex-row md:gap-10"><CheckoutReceipt hidePricing={isClaim} initialPrices={initialPrices} quote={data.buyQuote} /><Separator className="hidden h-auto md:block" orientation="vertical" /><CheckoutPaymentSection cardSupported={data.buyQuote.supportsFiatCheckout && !isClaim} currencySelector={<CurrencySelector className={isClaim ? "" : "mt-8"} defaultCurrency={defaultCurrency} nftChain={itemsWithPricing[0].chain.identifier} onBalancesReady={() => setBalancesReady(true)} onSelect={setSelectedCurrency} onWalletSelect={setWalletDestination} relayerFulfillment={data.buyQuote.supportsRelayerFulfillment} totalUsdPrice={totalUsdPrice ?? 0} walletDestination={walletDestination} />} hidePaymentMethod={isClaim} timeline={<CheckoutTimeline onAllActionsCompleted={handleAllActionsCompleted} priceValidation={priceValidation} quote={data.buyQuote} selectedCurrency={selectedCurrency} selectedCurrencyLoading={!balancesReady} walletDestination={walletDestination} />} /></Flex></BottomSheetBody></>}</>;
    $[24] = allActionsCompleted;
    $[25] = balancesReady;
    $[26] = data;
    $[27] = defaultCurrency;
    $[28] = handleAllActionsCompleted;
    $[29] = initialPrices;
    $[30] = isClaim;
    $[31] = itemsWithPricing[0];
    $[32] = priceValidation;
    $[33] = selectedCurrency;
    $[34] = t;
    $[35] = totalUsdPrice;
    $[36] = walletDestination;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  return t10;
}
function _temp2(item_0) {
  return item_0.priceDifference > item_0.initialPrice * MAX_PRICE_CHANGE_PERCENTAGE / 100;
}
function _temp(item) {
  return {
    item: {
      tokenId: item.tokenId,
      contractAddress: item.contractAddress,
      chain: item.chain.identifier
    },
    quantity: item.quantity
  };
}